import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@/lib/navigation";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { cloneVersion, createVersion } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";

/** Create a new version, or clone an existing one (`cloneFrom` set). */
export function VersionDialog({
  open,
  onOpenChange,
  agentSlug,
  cloneFrom,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentSlug: string;
  cloneFrom?: string;
}) {
  const qc = useQueryClient();
  const router = useRouter();
  const cloning = !!cloneFrom;
  const [version, setVersion] = React.useState("");
  const [tag, setTag] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setVersion("");
      setTag(cloning ? "" : "latest");
    }
  }, [open, cloning]);

  const mutation = useMutation({
    mutationFn: () =>
      cloning
        ? cloneVersion(agentSlug, cloneFrom!, { version, tag: tag || null })
        : createVersion(agentSlug, { version, tag: tag || null }),
    onSuccess: async (v) => {
      toast.success(cloning ? "Version cloned." : "Version created.");
      await qc.invalidateQueries({ queryKey: qk.versions(agentSlug) });
      await qc.invalidateQueries({ queryKey: qk.myAgents });
      onOpenChange(false);
      router.push(`/agents/${agentSlug}/versions/${v.version}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{cloning ? `Clone version ${cloneFrom}` : "New version"}</DialogTitle>
          <DialogDescription>
            {cloning
              ? "Copies the config + skills into a new version (status: draft)."
              : "A version holds the runtime config. Use semver, e.g. 1.0.0."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (version.trim()) mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="v-version">Version</Label>
            <Input
              id="v-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="v-tag">Tag (optional)</Label>
            <Input
              id="v-tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="latest"
            />
            <p className="text-xs text-muted-foreground">
              A movable pointer. Setting a tag already used by another version moves it here.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!version.trim() || mutation.isPending}>
              {mutation.isPending ? "Working…" : cloning ? "Clone" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
